import { createElement, useState } from "react";
import { createPortal } from "react-dom";
import Loader from "../components/Loader";
import { getRatePlansByProperty, getRoomsByProperty } from "../api";
import { DEFAULT_CALCULATION_TYPE } from "../pages/promotions/constants";

/**
 * Owns the Create Promo drawer for an Action Centre card.
 *
 * Both the v1 PromoCard and the v2 LlmActionCard open the same shared
 * {@link PromotionCreatePromo} drawer, and each had its own identical copy of the ten
 * useState calls behind it, the rate-plans/rooms fetch, and the twenty-prop mount. What
 * genuinely differs between them is only how the seed promo is BUILT — v1 reads
 * promoObject/promoObjectDr off a v1 recommendation, v2 reads promo_object_dr off a card
 * — so that stays with each caller and everything else lives here.
 *
 * Usage:
 *   const drawer = useCreatePromoDrawer();
 *   drawer.open(seededPromo, calculationType);   // after building the seed
 *   await drawer.loadRatePlansAndRooms(propertyId);
 *   {drawer.isOpen && <PromotionCreatePromo {...drawer.drawerProps} ... />}
 */
const useCreatePromoDrawer = () => {
  const [modal, setModal] = useState(false);
  const [promo, setPromo] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isMarket, setIsMarket] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [calculationType, setCalculationType] = useState(
    DEFAULT_CALCULATION_TYPE
  );
  const [loading, setLoading] = useState(false);
  const [ratePlans, setRatePlans] = useState([]);
  const [rooms, setRooms] = useState([]);
  // Property the rate plans/rooms above belong to. Without it every combo the
  // create-promo form builds is untagged, and an untagged combo is assumed to
  // belong to whichever property is submitted — which is how this flow sent
  // one property's rateplan ids under another property's id.
  const [ratePlansPropertyId, setRatePlansPropertyId] = useState(null);

  /**
   * Opens the drawer on a promo the caller has already seeded. isMarket/isEdit are both
   * true for every Action Centre card: the drawer is being opened on a suggestion, not a
   * blank form, and not on an existing promo being edited.
   */
  const open = (seedPromo, seedCalculationType = DEFAULT_CALCULATION_TYPE) => {
    setPromo(seedPromo);
    setCalculationType(seedCalculationType);
    setIsMarket(true);
    setIsEdit(true);
    setModal(true);
  };

  /**
   * Loads the property's rate plans and rooms into the drawer. Deliberately non-fatal:
   * the drawer is already open by this point, so a failure leaves it on promo defaults
   * rather than tearing down a modal the user just opened.
   */
  const loadRatePlansAndRooms = async (propertyId) => {
    if (!propertyId) return;
    try {
      const [ratePlansRes, roomsRes] = await Promise.all([
        getRatePlansByProperty(propertyId),
        getRoomsByProperty(propertyId),
      ]);
      setRatePlans(ratePlansRes.data.data);
      setRooms(roomsRes.data.data);
      setRatePlansPropertyId(propertyId);
    } catch {
      // rate plans/rooms fetch failed — drawer stays open with promo defaults
    }
  };

  return {
    isOpen: modal,
    loading,
    setLoading,
    open,
    loadRatePlansAndRooms,
    /**
     * The shared Loader, rendered into document.body rather than where the card sits.
     *
     * `.loader-overlay` is position:fixed with z-index 9999, which clears rsuite's modal (1050)
     * easily — but only from the same stacking context. The Action Centre carousel track carries
     * an inline `transform: translateX(...)`, and a transform other than `none` CREATES a
     * stacking context: the overlay is sealed inside it, its z-index ranks only against its
     * siblings, and position:fixed resolves against that ancestor instead of the viewport (the
     * track's `overflow: hidden` then clips it). rsuite portals the modal to document.body,
     * outside all of it, so the modal covered the spinner.
     *
     * It looked fine before v2 because the section is only a carousel above MAX_CARDS (3) cards;
     * at three or fewer it renders `.action-center-cards-grid`, which has no transform. The v2
     * cards pushed properties past three.
     *
     * Rendered from the hook so both Create Promo callers get it without a second loader
     * component, and createElement rather than JSX to keep this file .js.
     */
    loader: loading
      ? createPortal(createElement(Loader, { loading: true }), document.body)
      : null,
    /**
     * Everything PromotionCreatePromo needs that is the same for both callers. Each
     * caller adds `property`, `isPilotHotel`, `setIsGetPromos`, and (v2 only)
     * `onRecommendationUsed` — the props that are genuinely theirs.
     */
    drawerProps: {
      ratePlans,
      rooms,
      ratePlansPropertyId,
      modal,
      setModal,
      promo,
      setPromo,
      isEdit,
      setIsEdit,
      isMarket,
      setIsMarket,
      isDuplicate,
      setIsDuplicate,
      calculationType,
      setCalculationType,
      setLoading,
    },
  };
};

export default useCreatePromoDrawer;
